// Tab "Planning": call-info voor de draaidag en een eenvoudige draai- en postplanning.
import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { biedOngedaanAan } from '../../../utils/ongedaan';
import { meldFout } from '../../../utils/apiFout';
import { haalPlanning, wijzigPlanning } from '../breakdownApi';
import { Kaart, Kop, Secundair } from '../onderdelen';

const LEEG_DRAAI = { start: '', end: '', label: '' };
const LEEG_POST = { fase: '', datum: '' };
const HERSTELD = 'breakdown-planning-hersteld';
const VELDEN = ['call_time', 'call_location', 'weather', 'emergency_info', 'shoot_rows', 'post_rows'];

export default function PlanningTab({ productie }) {
  const [planning, setPlanning] = useState(null);
  const [concept, setConcept] = useState(null);
  // Het concept (wat op het scherm staat) is leidend. Opslaan gaat via één wachtrij en stuurt
  // het concept zoals het is op het moment dat de PATCH écht vertrekt, vergeleken met de laatst
  // door de server bevestigde planning. Zo kan geen oudere lijst een nieuwere overschrijven en
  // wist een serverantwoord nooit wat er intussen is getypt.
  const conceptRef = useRef(null);
  const bevestigdRef = useRef(null);
  const wachtrij = useRef(Promise.resolve());
  const gemount = useRef(true);
  useEffect(() => () => { gemount.current = false; }, []);

  // Ongedaan maken vanuit een eerder (inmiddels verlaten) tabblad: de actieve instantie zet de
  // regel zelf terug in haar concept en slaat op via haar eigen wachtrij.
  useEffect(() => {
    function opHerstel(e) {
      const d = e.detail || {};
      if (d.productieId !== productie.id || !conceptRef.current) return;
      d.afgehandeld = true;
      d.resultaat = (() => {
        wijzigConcept((c) => ({ ...c, [d.lijst]: d.terugzetten(c[d.lijst]) }));
        return bewaar();
      })();
    }
    window.addEventListener(HERSTELD, opHerstel);
    return () => window.removeEventListener(HERSTELD, opHerstel);
    // bewaar/wijzigConcept werken alleen via refs en productie.id; geen extra afhankelijkheden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productie.id]);

  useEffect(() => {
    let weg = false;
    haalPlanning(productie.id)
      .then((p) => {
        if (weg) return;
        conceptRef.current = p; bevestigdRef.current = p;
        setPlanning(p); setConcept(p);
      })
      .catch((e) => meldFout(e, 'Planning laden'));
    return () => { weg = true; };
  }, [productie.id]);

  function wijzigConcept(fn) {
    conceptRef.current = fn(conceptRef.current);
    setConcept(conceptRef.current);
  }

  // Slaat altijd álle afwijkende velden op (ook die van een eerder mislukte poging).
  function bewaar() {
    const stap = wachtrij.current.then(async () => {
      const nu = conceptRef.current;
      const patch = {};
      VELDEN.forEach((k) => {
        if (JSON.stringify(nu[k]) !== JSON.stringify(bevestigdRef.current[k])) patch[k] = nu[k];
      });
      if (Object.keys(patch).length === 0) return bevestigdRef.current;
      try {
        const p = await wijzigPlanning(productie.id, patch);
        bevestigdRef.current = p;
        setPlanning(p);
        return p;
      } catch (e) { meldFout(e, 'Planning opslaan'); return null; }
    });
    wachtrij.current = stap;
    return stap;
  }

  if (!planning || !concept) return null;

  const tekst = (veld, label, props = {}) => (
    <TextField label={label} size="small" fullWidth value={concept[veld]} {...props}
      onChange={(e) => { const v = e.target.value; wijzigConcept((c) => ({ ...c, [veld]: v })); }}
      onBlur={() => bewaar()} />
  );

  function rijVeld(lijst, i, veld, props = {}) {
    return (
      <TextField size="small" value={concept[lijst][i][veld]} {...props}
        onChange={(e) => { const v = e.target.value; wijzigConcept((c) => ({ ...c, [lijst]: c[lijst].map((r, j) => (j === i ? { ...r, [veld]: v } : r)) })); }}
        onBlur={() => bewaar()} />
    );
  }

  function rijErbij(lijst, leeg) {
    wijzigConcept((c) => ({ ...c, [lijst]: [...c[lijst], leeg] }));
    bewaar();
  }

  async function rijWeg(lijst, i) {
    const weg = conceptRef.current[lijst][i];
    wijzigConcept((c) => ({ ...c, [lijst]: c[lijst].filter((_, j) => j !== i) }));
    if (!(await bewaar())) return;
    const terugzetten = (l) => { const n = [...l]; n.splice(Math.min(i, n.length), 0, weg); return n; };
    biedOngedaanAan('Regel verwijderd', async () => {
      // Alleen deze regel terugzetten op zijn plek; latere wijzigingen blijven staan.
      if (gemount.current) {
        wijzigConcept((c) => ({ ...c, [lijst]: terugzetten(c[lijst]) }));
        return bewaar();
      }
      // Tab al verlaten: vers ophalen en op de actuele serverlijst terugzetten.
      const detail = { productieId: productie.id, lijst, terugzetten };
      window.dispatchEvent(new CustomEvent(HERSTELD, { detail }));
      if (detail.afgehandeld) return detail.resultaat;
      // Planning-tab niet open: vers ophalen en op de actuele serverlijst terugzetten.
      const vers = await haalPlanning(productie.id);
      return wijzigPlanning(productie.id, { [lijst]: terugzetten(vers[lijst]) });
    });
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Kop>Call</Kop>
        <Kaart sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, gap: 1.5 }}>
          {tekst('call_time', 'Call-tijd', { placeholder: '07:00' })}
          {tekst('call_location', 'Verzamelplek')}
          <Box sx={{ gridColumn: '1 / -1' }}>{tekst('weather', 'Weersverwachting')}</Box>
          <Box sx={{ gridColumn: '1 / -1' }}>{tekst('emergency_info', 'Dichtstbijzijnd ziekenhuis / noodinfo', { multiline: true, minRows: 2 })}</Box>
        </Kaart>
      </Box>

      <Box>
        <Kop rechts={<Secundair onClick={() => rijErbij('shoot_rows', LEEG_DRAAI)}>+ Regel</Secundair>}>Draaiplanning</Kop>
        <Kaart sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {concept.shoot_rows.length === 0 && <Typography sx={{ fontSize: 13, color: '#5f6b7a' }}>Nog geen tijdsblokken.</Typography>}
          {concept.shoot_rows.map((_, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {rijVeld('shoot_rows', i, 'start', { placeholder: 'Start', sx: { width: 90 } })}
              {rijVeld('shoot_rows', i, 'end', { placeholder: 'Eind', sx: { width: 90 } })}
              {rijVeld('shoot_rows', i, 'label', { placeholder: 'Wat', fullWidth: true })}
              <IconButton size="small" aria-label="Regel verwijderen" onClick={() => rijWeg('shoot_rows', i)}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Box>
          ))}
        </Kaart>
      </Box>

      <Box>
        <Kop rechts={<Secundair onClick={() => rijErbij('post_rows', LEEG_POST)}>+ Regel</Secundair>}>Postplanning</Kop>
        <Kaart sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {concept.post_rows.length === 0 && <Typography sx={{ fontSize: 13, color: '#5f6b7a' }}>Nog geen fases.</Typography>}
          {concept.post_rows.map((_, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {rijVeld('post_rows', i, 'datum', { type: 'date', sx: { width: 170 }, InputLabelProps: { shrink: true } })}
              {rijVeld('post_rows', i, 'fase', { placeholder: 'Fase, bv. offline of kleurcorrectie', fullWidth: true })}
              <IconButton size="small" aria-label="Regel verwijderen" onClick={() => rijWeg('post_rows', i)}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Box>
          ))}
        </Kaart>
      </Box>
    </Box>
  );
}
